import {err, ok, ValidationError} from 'framework';
import type {Result, Validator} from 'framework';
import * as z from 'zod';
import type {CreateManualMealEntryCommand} from '../../domain/create-manual-meal-command';
import {FOOD_QUANTITY_UNITS} from '../../domain/food-quantity.value-object';
import {MANUAL_MEAL_ENTRY_SOURCES} from '../../domain/manual-meal-entry-source.value-object';
import {MAX_NUTRITION_PER_100G} from '../../domain/nutrition-per-100g.value-object';
import type {ProductId} from '../../domain/product-id.value-object';

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

const MAX_DISPLAY_NAME = 200;
const MAX_GRAM_EQUIVALENT = 10_000;
const MAX_KCAL = 10_000;
const MAX_MACRO_GRAMS = 1_000;
const MAX_NOTES = 2_000;
const MAX_QUANTITY = 10_000;

const nutritionPer100gSchema = z.strictObject({
  kcal: z.number().finite().min(0).max(MAX_NUTRITION_PER_100G),
  proteinGrams: z.number().finite().min(0).max(MAX_NUTRITION_PER_100G),
  carbsGrams: z.number().finite().min(0).max(MAX_NUTRITION_PER_100G),
  fatGrams: z.number().finite().min(0).max(MAX_NUTRITION_PER_100G),
});

const originSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('user_product'),
    productId: z.custom<ProductId>(isNonEmptyString, 'Invalid product id.'),
  }),
  z.strictObject({kind: z.literal('off'), barcode: z.string().min(1)}),
  z.strictObject({kind: z.literal('manual')}),
]);

export class ManualMealEntryDataValidator implements Validator<CreateManualMealEntryCommand> {
  readonly standardSchema = z.strictObject({
    displayName: z.string().trim().min(1).max(MAX_DISPLAY_NAME),
    quantity: z.number().finite().min(0).max(MAX_QUANTITY),
    unit: z.enum(FOOD_QUANTITY_UNITS),
    gramEquivalent: z.number().finite().min(0).max(MAX_GRAM_EQUIVALENT).nullable(),
    kcal: z.number().finite().min(0).max(MAX_KCAL),
    proteinGrams: z.number().finite().min(0).max(MAX_MACRO_GRAMS),
    carbsGrams: z.number().finite().min(0).max(MAX_MACRO_GRAMS),
    fatGrams: z.number().finite().min(0).max(MAX_MACRO_GRAMS),
    notes: z.string().max(MAX_NOTES).nullable(),
    source: z.enum(MANUAL_MEAL_ENTRY_SOURCES),
    nutritionPer100g: nutritionPer100gSchema.nullable(),
    origin: originSchema.nullable(),
  });

  validate(value: unknown): Result<CreateManualMealEntryCommand, ValidationError> {
    const parsed = this.standardSchema.safeParse(value);
    if (!parsed.success) {
      return err(new ValidationError(parsed.error.issues[0]?.message ?? 'Invalid meal entry.'));
    }
    return ok(parsed.data);
  }
}
